import { MODAL_MAIN_CONTENT_HEIGHT } from 'src/pages/consumer/components/DishFirstHeader';
import { ArrowSoftLeft, ArrowSoftRight, ChevronDownV2 } from 'src/shared/design-system/Icon';
import { Clickable } from 'src/shared/design-system/Utilities';
import { VerticallyCenteredText } from 'src/shared/design-system/VerticallyCenteredText';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { Border, Colors, Font, Mixins, Spacing, Transition } from 'web-common/src/shared/styles';

export const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacing.SINGLE_HALF};
  padding: ${Spacing.DOUBLE};
  overflow: scroll;

  ${Mixins.Media.maxWidthSmall(`
    flex-direction: column;
    height: ${MODAL_MAIN_CONTENT_HEIGHT}
  `)}
`;

export const DatePickerWeekWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacing.HALF};
  width: 100%;

  &:not(:first-of-type) {
    padding: ${Spacing.NONE} ${Spacing.NONE} ${Spacing.NONE} ${Spacing.SINGLE_HALF};
    border-left: 1px solid ${Colors.GRAY_LITE_10};
  }

  ${Mixins.Media.maxWidthSmall(`
    flex-wrap: wrap;

    &:not(:first-of-type) {
      padding: ${Spacing.SINGLE_HALF} ${Spacing.NONE} ${Spacing.NONE};
      border-top: 1px solid ${Colors.GRAY_LITE_10};
      border-left: none;
    }
  `)}
`;

export const DatePickerWeekHeader = styled.div`
  font-size: ${Font.Size.S};
  font-weight: ${Font.Weight.SEMI_BOLD};
  margin-bottom: ${Spacing.QUARTER};
`;

type ButtonContainerProps = { active: boolean; isPastDate?: boolean; isUnavailable?: boolean };

export const ButtonContainer = styled.button<ButtonContainerProps>`
  align-items: flex-start;
  background: none;
  cursor: pointer;
  user-select: none;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 104px;
  width: 112px;
  justify-content: center;
  margin-left: -1px;
  flex: 1 1 100%;
  border: 1px solid ${Colors.GRAY_LITE_10};
  padding: ${Spacing.SINGLE};
  border-radius: ${Border.Radius.M};

  ${({ active = false, isPastDate = false, isUnavailable = false }) =>
    match<ButtonContainerProps>({ active, isPastDate, isUnavailable })
      .with(
        { active: true },
        () => css`
          background: ${Colors.KOHLRABI_LITE_40};
          border-color: ${Colors.KOHLRABI_DARK_10};
        `
      )
      .with(
        { isPastDate: true },
        () => css`
          display: none;
        `
      )
      .with(
        { isPastDate: false, isUnavailable: true },
        () => css`
          background: ${Colors.GRAY_LITE_30};
          text-decoration: strike-through;
          pointer-events: none;
        `
      )
      .otherwise(() => css``)}

  ${Mixins.Media.maxWidthSmall(`
    width: 100%;
    max-width: 100%;
    height: ${Spacing.QUADRUPLE};
  `)}
`;

export const ButtonContainerWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
  justify-content: space-between;

  ${Mixins.Media.maxWidthSmall(`
    flex-direction: row;
    flex: 0;
  `)}
`;

export const ButtonDateWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Mixins.Media.maxWidthSmall(`
    gap: ${Spacing.HALF};
    flex-direction: row;
    align-items: center;
  `)}
`;

type ButtonWeekDayProps = { isPastDate?: boolean; isUnavailable?: boolean };

export const ButtonWeekDay = styled.span<ButtonWeekDayProps>`
  font-family: ${Font.Family.SANS};
  font-size: ${Font.Size.M};
  font-weight: ${Font.Weight.BOLD};
  color: ${Colors.GRAY_DARK_30};

  ${({ isPastDate = false, isUnavailable = false }) =>
    match<ButtonWeekDayProps>({ isPastDate, isUnavailable })
      .with(
        { isPastDate: true },
        () => css`
          color: ${Colors.GRAY_DARK_10};
          text-decoration: line-through;
        `
      )
      .with(
        { isPastDate: false, isUnavailable: true },
        () => css`
          color: ${Colors.GRAY_LITE_10};
        `
      )
      .otherwise(() => ``)}
`;

type ButtonDateProps = { isPastDate?: boolean; isUnavailable?: boolean };

export const ButtonDate = styled.span<ButtonDateProps>`
  font-family: ${Font.Family.SANS};
  font-size: ${Font.Size.S};
  font-weight: ${Font.Weight.NORMAL};
  color: ${Colors.GRAY_DARK_30};

  ${({ isPastDate = false, isUnavailable = false }) =>
    match<ButtonDateProps>({ isPastDate, isUnavailable })
      .with(
        { isPastDate: true },
        () => css`
          color: ${Colors.GRAY_DARK_10};
          text-decoration: line-through;
        `
      )
      .with(
        { isPastDate: false, isUnavailable: true },
        () => css`
          color: ${Colors.GRAY_LITE_10};
        `
      )
      .otherwise(() => ``)}
`;

export const ButtonAvailability = styled.span`
  font-family: ${Font.Family.SANS};
  font-size: ${Font.Size.XXS};
  font-weight: ${Font.Weight.SEMI_BOLD};
  color: ${Colors.GRAY_DARK_15};
`;

export const InlineDropdownContainer = styled(VerticallyCenteredText)``;

export const DropdownTextContainer = styled(VerticallyCenteredText)<{ isLargeText: boolean }>`
  white-space: nowrap;
  font-size: ${ifProp('isLargeText', '24px', '')};
`;

export const DropdownLink = styled(Clickable)`
  align-items: center;
  display: flex;
  font-family: ${Font.Family.SANS};
  font-size: ${Font.Size.S};
  padding: ${Spacing.QUARTER};
  gap: ${Spacing.HALF};

  ${InlineDropdownContainer} & {
    font-size: ${Font.Size.XL};
  }
`;

export const DropdownArrowContainer = styled.div<{ isOpen?: boolean }>`
  align-items: center;
  display: flex;
  transform: ${ifProp('isOpen', 'rotate(180deg)', 'rotate(0deg)')};
  transition: transform ${Transition.Time.BASE};
`;

export const DropdownArrow = styled(ChevronDownV2)`
  width: ${Spacing.SINGLE};
`;

export const NotAvailableText = styled.div`
  font-size: ${Font.Size.XXS};
  color: ${Colors.GRAY_DARK_20};

  ${Mixins.Media.maxWidthSmall(`
    flex: 0;
    color: ${Colors.GRAY_DARK_10};
  `)}
`;

const BUTTON_SIZE = 40;
const ARROW_SIZE = 16;

const CarouselGradient = styled.div<{ isVisible: boolean }>`
  position: absolute;
  z-index: 1;
  height: 168px;
  width: 72px;
  transition: transform 0.1s ease-in-out;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`;
export const LeftCarouselGradient = styled(CarouselGradient)`
  left: 0;
  background: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, ${Colors.WHITE} 100%);
`;
export const RightCarouselGradient = styled(CarouselGradient)`
  right: 0;
  background: linear-gradient(270deg, ${Colors.WHITE} 0%, rgba(255, 255, 255, 0) 100%);
`;

const ScrollButtonContainer = styled.div<{ isVisible: boolean }>`
  width: ${BUTTON_SIZE}px;
  height: ${BUTTON_SIZE}px;
  position: absolute;
  margin: 68px 16px 0px;
  z-index: 1;
  cursor: ${({ isVisible }) => (isVisible ? 'pointer' : 'unset')};

  transition: transform 0.1s ease-in-out;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};

  ${Mixins.Media.maxWidthSmall(`
    display: none;
  `)}
`;
export const LeftButtonContainer = styled(ScrollButtonContainer)`
  left: 0;
`;
export const RightButtonContainer = styled(ScrollButtonContainer)`
  right: 0;
`;

export const ScrollButton = styled.div`
  width: ${BUTTON_SIZE}px;
  height: ${BUTTON_SIZE}px;
  border: 1.25px solid ${Colors.GRAY_LITE_20};
  border-radius: ${BUTTON_SIZE}px;

  background: ${Colors.WHITE};
  opacity: 1;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export const StyledArrowRight = styled(ArrowSoftRight)`
  position: absolute;
  top: ${(BUTTON_SIZE - ARROW_SIZE) / 2}px;
  left: ${(BUTTON_SIZE - ARROW_SIZE) / 2}px;
  width: ${ARROW_SIZE};
  height: ${ARROW_SIZE};
`;

export const StyledArrowLeft = styled(ArrowSoftLeft)`
  position: absolute;
  top: ${(BUTTON_SIZE - ARROW_SIZE) / 2}px;
  left: ${(BUTTON_SIZE - ARROW_SIZE) / 2}px;
  width: ${ARROW_SIZE};
  height: ${ARROW_SIZE};
`;
